'use client';

import { useGSAP } from '@gsap/react';
import clsx from 'clsx';
import { FC, MouseEvent, useRef } from 'react';

import { BehindSceneDto } from '@/components/service-pages/BehindScene/types';
import { Typography } from '@/components/shared/Typography';
import StrapiRichContent from '@/components/StrapiRichContent';
import { gsap } from '@/lib/gsap';

import css from './style.module.scss';

interface Props {
	data: BehindSceneDto;
}

const BehindScene: FC<Props> = ({ data }) => {
	const containerRef = useRef<HTMLElement>(null);
	const positionsRefs = useRef<(HTMLUListElement | null)[]>([]);

	useGSAP(
		() => {
			positionsRefs.current.forEach((ul, index) => {
				if (!ul) return;

				const itemCount = data.cards[index].positions.length;

				if (itemCount < 5) {
					ul.style.maxHeight = 'none';
					return;
				}

				const firstItem = ul.querySelector('li');
				if (!firstItem) return;

				const itemHeight = firstItem.offsetHeight;
				const gap = 5;

				const itemsPerColumn = Math.ceil(itemCount / 2);

				const maxHeight = itemsPerColumn * itemHeight + (itemsPerColumn - 1) * gap;

				ul.style.maxHeight = `${maxHeight}px`;
			});

			const titleLines = containerRef.current?.querySelectorAll(`.${css.title} p`);
			const cardsElements = containerRef.current?.querySelectorAll(`.${css.card}`);

			if (titleLines?.length) gsap.set(titleLines, { opacity: 0, y: 40, filter: 'blur(10px)' });

			if (cardsElements?.length) gsap.set(cardsElements, { opacity: 0 });

			const tl = gsap.timeline({
				scrollTrigger: {
					trigger: containerRef.current,
					start: 'top 70%',
					toggleActions: 'play none none reverse'
				}
			});

			if (titleLines?.length)
				tl.to(titleLines, {
					opacity: 1,
					y: 0,
					filter: 'blur(0px)',
					duration: 1,
					stagger: 0.15,
					ease: 'power3.out'
				});

			if (cardsElements?.length)
				tl.to(
					cardsElements,
					{
						opacity: 1,
						duration: 0.8,
						stagger: 0.1,
						ease: 'power2.out'
					},
					'-=0.5'
				);
		},
		{ scope: containerRef }
	);

	const handleMouseMove = (e: MouseEvent<HTMLDivElement>) => {
		const cards = e.currentTarget;
		const rect = cards.getBoundingClientRect();
		const x = e.clientX - rect.left;
		const y = e.clientY - rect.top;

		cards.style.setProperty('--mouse-x', `${x}px`);
		cards.style.setProperty('--mouse-y', `${y}px`);
	};

	return (
		<section ref={containerRef} className={clsx(css.section, 'container')}>
			<Typography uppercase variant="h2" tag="h2" className={css.title}>
				<StrapiRichContent content={data.title} />
			</Typography>

			<div className={css.cards} onMouseMove={handleMouseMove}>
				{data.cards.map((card, cardIndex) => (
					<div key={card.label} className={css.card}>
						<Typography uppercase variant="body-s" tag="p" className={css.label}>
							{card.label}
						</Typography>

						<Typography variant="h6" tag="div" className={css.cardTitle}>
							<StrapiRichContent content={card.title} />
						</Typography>

						<ul
							ref={el => {
								positionsRefs.current[cardIndex] = el;
							}}
							className={css.positions}
						>
							{card.positions.map(({ position }) => (
								<Typography key={position} variant="body-s" tag="li">
									{position}
								</Typography>
							))}
						</ul>
					</div>
				))}
			</div>
		</section>
	);
};

export default BehindScene;
